/**
 * The grid behind a month view.
 *
 * Plain calendar dates throughout — a hatch date is a day, not an instant — so every
 * date here is built and read in UTC. Using local getters is the classic way a picker
 * shows the 30th to a breeder in Auckland and stores the 29th: `new Date("2026-03-01")`
 * parses as midnight UTC, and `.getDate()` on that is the last day of February anywhere
 * west of Greenwich.
 */

import type { PlainDate } from "./bird-age";

const MS_PER_DAY = 86_400_000;

/** A month as `YYYY-MM`. */
export type PlainMonth = string;

export interface CalendarDay {
  /** `YYYY-MM-DD`. */
  iso: PlainDate;
  /** Day of the month, 1–31. */
  day: number;
  /** False for the leading and trailing days borrowed from a neighbouring month. */
  inMonth: boolean;
}

function pad(value: number): string {
  return value.toString().padStart(2, "0");
}

/**
 * `YYYY-MM-DD`, checked properly.
 *
 * Splitting on the dash and taking three numbers is not enough: "26-08-2026" passes
 * that test and yields a date in the year 26, silently, which is exactly the kind of
 * wrong a hatch date should never be allowed to be.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDate(date: PlainDate): { year: number; month: number; day: number } {
  const match = ISO_DATE.exec(date);

  if (!match) {
    throw new RangeError(`Expected a YYYY-MM-DD date, got "${date}".`);
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new RangeError(`"${date}" is not a real calendar date.`);
  }

  return { year, month, day };
}

const ISO_MONTH = /^(\d{4})-(\d{2})$/;

function parseMonth(month: PlainMonth): { year: number; month: number } {
  const match = ISO_MONTH.exec(month);

  if (!match) {
    throw new RangeError(`Expected a YYYY-MM month, got "${month}".`);
  }

  const year = Number(match[1]);
  const monthNumber = Number(match[2]);

  if (monthNumber < 1 || monthNumber > 12) {
    throw new RangeError(`"${month}" is not a real month.`);
  }

  return { year, month: monthNumber };
}

/** The month a date falls in. */
export function monthOf(date: PlainDate): PlainMonth {
  return date.slice(0, 7);
}

/** Steps a month forward or back, rolling the year over. */
export function addMonths(month: PlainMonth, delta: number): PlainMonth {
  const { year, month: monthNumber } = parseMonth(month);
  const zeroBased = monthNumber - 1 + delta;

  // Floor division, so a negative delta rolls the year back rather than towards zero.
  const yearsMoved = Math.floor(zeroBased / 12);
  const finalMonth = zeroBased - yearsMoved * 12;

  return `${year + yearsMoved}-${pad(finalMonth + 1)}`;
}

/** Steps a date by whole days. */
export function addDays(date: PlainDate, delta: number): PlainDate {
  const { year, month, day } = parseDate(date);

  return toIso(new Date(Date.UTC(year, month - 1, day) + delta * MS_PER_DAY));
}

function toIso(instant: Date): PlainDate {
  return `${instant.getUTCFullYear()}-${pad(instant.getUTCMonth() + 1)}-${pad(instant.getUTCDate())}`;
}

/**
 * Six weeks of days for a month, including the neighbouring days that fill the first
 * and last rows.
 *
 * Always forty-two cells, never five rows for one month and six for the next. A panel
 * that changes height as you page through it makes the arrows move under the finger
 * that is pressing them.
 *
 * `weekStartsOn` is 0 for Sunday through 6 for Saturday.
 */
export function monthGrid(month: PlainMonth, weekStartsOn = 1): CalendarDay[] {
  const { year, month: monthNumber } = parseMonth(month);

  const first = new Date(Date.UTC(year, monthNumber - 1, 1));
  const lead = (first.getUTCDay() - weekStartsOn + 7) % 7;
  const start = new Date(first.getTime() - lead * MS_PER_DAY);

  return Array.from({ length: 42 }, (_, index) => {
    const instant = new Date(start.getTime() + index * MS_PER_DAY);

    return {
      iso: toIso(instant),
      day: instant.getUTCDate(),
      inMonth: instant.getUTCMonth() === monthNumber - 1 && instant.getUTCFullYear() === year,
    };
  });
}

/**
 * The weekday initials for a row of headers, rotated to the week's first day.
 *
 * Taken from the runtime's own locale data rather than a hard-coded list, so a Dutch
 * breeder gets "ma di wo" and an English one "Mon Tue Wed" without the app shipping
 * either.
 */
export function weekdayNames(
  weekStartsOn = 1,
  locale?: string,
  format: "narrow" | "short" = "narrow",
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: format, timeZone: "UTC" });

  // 4 January 1970 was a Sunday, which makes it a convenient index-zero.
  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(new Date(Date.UTC(1970, 0, 4 + ((weekStartsOn + index) % 7)))),
  );
}

/**
 * Which day a week starts on in the reader's locale — Monday across most of Europe,
 * Sunday in the US. Falls back to Monday, which is what a Dutch or Belgian aviary
 * keeps its records by.
 */
export function firstDayOfWeek(locale?: string): number {
  try {
    const resolved = new Intl.Locale(locale ?? new Intl.NumberFormat().resolvedOptions().locale);

    // `getWeekInfo` is recent and not everywhere; its `firstDay` is 1–7 with Monday
    // first, against the 0–6 Sunday-first numbering `Date.getUTCDay` uses.
    const info = (
      resolved as Intl.Locale & { getWeekInfo?: () => { firstDay: number } }
    ).getWeekInfo?.();

    if (info) {
      return info.firstDay % 7;
    }
  } catch {
    // An unparseable locale is not worth failing a date field over.
  }

  return 1;
}
